'use client';

import { globalStore } from '@/app/globalStore';

type ReportEditorHookErrorOptions = {
  hook: string;
  error: unknown;
  context?: string;
  target?: unknown;
};

const FALLBACK_ERROR_MESSAGE = 'Unknown editor hook error';

function getTargetLabel(target: unknown): string {
  if (!target || typeof target !== 'object') {
    return 'unknown target';
  }

  const candidate = target as {
    name?: unknown;
    uuid?: unknown;
    constructor?: { name?: unknown };
  };
  const className = typeof candidate.constructor?.name === 'string' ? candidate.constructor.name : 'UnknownClass';
  const name = typeof candidate.name === 'string' && candidate.name.trim() ? candidate.name.trim() : undefined;
  const uuid = typeof candidate.uuid === 'string' && candidate.uuid.trim() ? candidate.uuid.trim() : undefined;

  if (name && uuid) {
    return `${className}(${name}, ${uuid})`;
  }
  if (name) {
    return `${className}(${name})`;
  }
  if (uuid) {
    return `${className}(${uuid})`;
  }
  return className;
}

function getErrorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function reportEditorHookError(options: ReportEditorHookErrorOptions): string {
  const targetLabel = getTargetLabel(options.target);
  const details = getErrorDetails(options.error).trim() || FALLBACK_ERROR_MESSAGE;
  const contextLabel = options.context ? `${options.context} · ` : '';
  const message = `[Editor Hook Error] ${contextLabel}${options.hook} failed on ${targetLabel}\n${details}`;

  console.error(message, options.error);
  globalStore.addBuildError(message);
  globalStore.setShowBuildErrorOverlay(true);
  return message;
}
